import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { portalGet, portalList, portalPost, portalTokens } from './client';

export type PortalSessionUser = {
  id: string;
  email: string;
  name: string;
  portalRole: 'ADMIN' | 'STANDARD';
  lastLoginAt: string | null;
  company: { id: string; name: string };
  organization: { id: string; name: string; baseCurrency: string; timezone: string };
};

type AuthResponse = {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: PortalSessionUser;
};

/** The signed-in client, or null. Every portal screen hangs off this. */
export function usePortalSession() {
  return useQuery({
    queryKey: ['portal', 'me'],
    queryFn: async () => {
      if (!portalTokens.access && !portalTokens.refresh) return null;
      try {
        return await portalGet<PortalSessionUser>('/portal/auth/me');
      } catch {
        return null;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function usePortalLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      portalPost<AuthResponse>('/portal/auth/login', input),
    onSuccess: (result) => {
      portalTokens.set(result);
      queryClient.setQueryData(['portal', 'me'], result.user);
    },
  });
}

export function usePortalAcceptInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      portalPost<AuthResponse>('/portal/auth/accept-invite', input),
    onSuccess: (result) => {
      portalTokens.set(result);
      queryClient.setQueryData(['portal', 'me'], result.user);
    },
  });
}

export function usePortalForgot() {
  return useMutation({
    mutationFn: (input: { email: string }) => portalPost('/portal/auth/forgot-password', input),
  });
}

export function usePortalReset() {
  return useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      portalPost('/portal/auth/reset-password', input),
  });
}

export function usePortalLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const refreshToken = portalTokens.refresh;
      if (refreshToken) {
        await portalPost('/portal/auth/logout', { refreshToken }).catch(() => undefined);
      }
      portalTokens.clear();
    },
    onSuccess: () => queryClient.clear(),
  });
}

// ── The data ────────────────────────────────────────────────────────────────

export type PortalDashboard = {
  projects: {
    id: string;
    number: string;
    name: string;
    status: string;
    health: string;
    targetEndDate: string | null;
    milestoneCount: number;
    milestonesDone: number;
  }[];
  openTickets: number;
  contracts: { id: string; number: string; name: string; endDate: string; status: string }[];
  recentTickets: {
    id: string;
    number: string;
    subject: string;
    status: string;
    priority: string;
    createdAt: string;
    resolutionDueAt: string | null;
  }[];
  upcomingMilestones: {
    id: string;
    name: string;
    plannedEnd: string | null;
    status: string;
    percentComplete: number;
    project: { id: string; name: string };
  }[];
  finance: { outstanding: string | null; overdue: string | null; openInvoiceCount: number } | null;
};

export function usePortalDashboard() {
  return useQuery({
    queryKey: ['portal', 'dashboard'],
    queryFn: () => portalGet<PortalDashboard>('/portal/dashboard'),
  });
}

export type PortalProject = {
  id: string;
  number: string;
  name: string;
  description: string | null;
  status: string;
  health: string;
  startDate: string | null;
  targetEndDate: string | null;
  actualEndDate?: string | null;
  manager: { name: string; designation: string | null; email?: string } | null;
  milestones?: {
    id: string;
    name: string;
    description: string | null;
    deliverables: string | null;
    plannedEnd: string | null;
    actualEnd: string | null;
    percentComplete: number;
    status: string;
    signedOffAt: string | null;
    signedOffBy: string | null;
    signOffNote: string | null;
  }[];
};

export function usePortalProjects() {
  return useQuery({
    queryKey: ['portal', 'projects'],
    queryFn: () => portalList<PortalProject[]>('/portal/projects'),
  });
}

export function usePortalProject(id: string | undefined) {
  return useQuery({
    queryKey: ['portal', 'project', id],
    queryFn: () => portalGet<PortalProject>(`/portal/projects/${id}`),
    enabled: Boolean(id),
  });
}

export function useSignOffMilestone(projectId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, approved, comment }: { id: string; approved: boolean; comment?: string }) =>
      portalPost(`/portal/milestones/${id}/sign-off`, { approved, comment }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal', 'project', projectId] }),
  });
}

export type PortalTicket = {
  id: string;
  number: string;
  subject: string;
  description?: string;
  status: string;
  priority: string;
  ticketType: string;
  createdAt: string;
  resolvedAt: string | null;
  closedAt?: string | null;
  firstResponseDueAt: string | null;
  resolutionDueAt: string | null;
  csatScore: number | null;
  csatComment?: string | null;
  assignee: { name: string; designation?: string | null } | null;
  project?: { id: string; name: string } | null;
  contract?: { id: string; number: string; name: string } | null;
  messages?: {
    id: string;
    body: string;
    fromClient: boolean;
    createdAt: string;
    author: { name: string } | null;
  }[];
};

export function usePortalTickets(status?: string) {
  return useQuery({
    queryKey: ['portal', 'tickets', status],
    queryFn: () =>
      portalList<PortalTicket[]>('/portal/tickets', {
        params: status ? { status } : undefined,
      }),
  });
}

export function usePortalTicket(id: string | undefined) {
  return useQuery({
    queryKey: ['portal', 'ticket', id],
    queryFn: () => portalGet<PortalTicket>(`/portal/tickets/${id}`),
    enabled: Boolean(id),
    refetchInterval: 30_000,
  });
}

export function useRaiseTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      subject: string;
      description: string;
      priority?: string;
      ticketType?: string;
      projectId?: string;
    }) => portalPost<PortalTicket>('/portal/tickets', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal'] }),
  });
}

export function useReplyToTicket(id: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => portalPost<PortalTicket>(`/portal/tickets/${id}/reply`, { body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal', 'ticket', id] }),
  });
}

export function useCloseTicket(id: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => portalPost<PortalTicket>(`/portal/tickets/${id}/close`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal'] }),
  });
}

export function useRateTicket(id: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { rating: number; comment?: string }) =>
      portalPost<PortalTicket>(`/portal/tickets/${id}/rate`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal', 'ticket', id] }),
  });
}

export type Entitlement = {
  id: string;
  number: string;
  name: string;
  endDate: string;
  slaPolicy: string | null;
  period: { start: string; end: string } | null;
  allocatedMinutes: number | null;
  consumedMinutes: number | null;
  availableMinutes: number | null;
};

export function usePortalEntitlement() {
  return useQuery({
    queryKey: ['portal', 'entitlement'],
    queryFn: () => portalGet<Entitlement[]>('/portal/entitlement'),
  });
}

export type PortalInvoice = {
  id: string;
  number: string;
  type: string;
  issueDate: string;
  dueDate: string;
  currency: string;
  grandTotal: string | null;
  amountPaid: string | null;
  balanceDue: string | null;
  status: string;
  publicToken: string;
  poReference: string | null;
};

export function usePortalInvoices(status?: string) {
  return useQuery({
    queryKey: ['portal', 'invoices', status],
    queryFn: () =>
      portalList<PortalInvoice[]>('/portal/invoices', {
        params: status ? { status } : undefined,
      }),
  });
}

export type PortalStatement = {
  from: string;
  to: string;
  opening: string | null;
  closing: string | null;
  lines: {
    id: string;
    date: string;
    kind: 'invoice' | 'payment' | 'refund';
    reference: string;
    detail: string;
    debit: string | null;
    credit: string | null;
    balance: string | null;
  }[];
};

export function usePortalStatement() {
  return useQuery({
    queryKey: ['portal', 'statement'],
    queryFn: () => portalGet<PortalStatement>('/portal/statement'),
  });
}

export function usePortalQuotes() {
  return useQuery({
    queryKey: ['portal', 'quotes'],
    queryFn: () =>
      portalGet<
        {
          id: string;
          number: string;
          version: number;
          issueDate: string;
          validUntil: string | null;
          currency: string;
          status: string;
          publicToken: string;
          acceptedAt: string | null;
          rejectedAt: string | null;
        }[]
      >('/portal/quotes'),
  });
}

export function useDecideQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, accept, comment }: { id: string; accept: boolean; comment?: string }) =>
      portalPost(`/portal/quotes/${id}/decision`, { accept, comment }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal', 'quotes'] }),
  });
}

export function usePortalContracts() {
  return useQuery({
    queryKey: ['portal', 'contracts'],
    queryFn: () =>
      portalGet<
        {
          id: string;
          number: string;
          name: string;
          contractType: string;
          startDate: string;
          endDate: string;
          status: string;
          autoRenew: boolean;
          coverageIncluded: string | null;
          coverageExcluded: string | null;
          slaPolicy: { name: string } | null;
          value?: string | null;
          currency?: string;
        }[]
      >('/portal/contracts'),
  });
}

export function usePortalSubscriptions() {
  return useQuery({
    queryKey: ['portal', 'subscriptions'],
    queryFn: () =>
      portalGet<
        {
          id: string;
          number: string;
          quantity: number;
          status: string;
          billingCycle: string;
          startDate: string;
          currentPeriodEnd: string;
          nextBillingDate: string | null;
          autoRenew: boolean;
          instanceUrl: string | null;
          product: { name: string };
          plan: { name: string };
          unitPrice?: string | null;
          currency?: string;
        }[]
      >('/portal/subscriptions'),
  });
}

export function useRaisePortalRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      requestType: string;
      subject: string;
      body: string;
      entityType?: string;
      entityId?: string;
    }) => portalPost('/portal/requests', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal'] }),
  });
}

export function usePortalDocuments() {
  return useQuery({
    queryKey: ['portal', 'documents'],
    queryFn: () =>
      portalGet<
        {
          id: string;
          name: string;
          description: string | null;
          category: string;
          entityType: string | null;
          updatedAt: string;
          currentVersion: {
            id: string;
            version: number;
            sizeBytes: string;
            mimeType: string;
            createdAt: string;
          } | null;
        }[]
      >('/portal/documents'),
  });
}

/** Fetches a signed link and opens it. The link expires; the URL is not stored. */
export async function downloadPortalDocument(id: string): Promise<void> {
  const { url } = await portalGet<{ url: string; name: string }>(`/portal/documents/${id}/download`);
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function useChangePortalPassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      portalPost('/portal/auth/change-password', input),
  });
}
